// ======================================================================
// File: FollowupDetail.jsx
// Purpose: Detail view + status change actions (Enterprise UI)
// ======================================================================

import React, { useEffect, useState } from "react";
import fmsService from "../../services/fmsService";
import { useParams } from "react-router-dom";
import "./FollowupDetail.css";

export default function FollowupDetail() {

  const { id } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const [newStatus, setNewStatus] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  const loadDetail = async () => {
    try {
      setLoading(true);
      const res = await fmsService.getFollowupById(id);
      if (res && res.data) setData(res.data);
      else setData(null);
    } catch (err) {
      console.error(err);
      alert("Failed to load followup detail");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) loadDetail();
  }, [id]);

  const handleChangeStatus = async () => {
    if (!newStatus) {
      alert("Please select status");
      return;
    }

    try {
      setSaving(true);

      if (
        newStatus === "COMPLETED" &&
        data &&
        data.is_recurring
      ) {
        const confirmNext = window.confirm(
          `This followup is configured as recurring.\n\nDo you want to create the next followup after ${data.recurrence_days} days?`
        );

        if (confirmNext) {
          await fmsService.createNextFollowup(id);
          alert("Followup completed and next followup created successfully");
          setNewStatus("");
          setRemarks("");
          loadDetail();
          return;
        }
      }

      await fmsService.changeStatus(id, {
        status_code: newStatus,
        remarks: remarks
      });

      alert("Status updated successfully");
      setNewStatus("");
      setRemarks("");
      loadDetail();

    } catch (err) {
      console.error(err);
      alert("Failed to update status");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-4">Loading...</div>;
  if (!data) return <div className="p-4">No data found</div>;

  const { actions = [], history = [] } = data;

  const isOverdue =
    data.due_date &&
    new Date(data.due_date) < new Date() &&
    data.status_code !== "COMPLETED";

  const getStatusBadgeClass = (status) => {
    const map = {
      OPEN: "bg-secondary",
      IN_PROGRESS: "bg-warning text-dark",
      COMPLETED: "bg-success",
      CANCELLED: "bg-danger"
    };
    return map[status] || "bg-secondary";
  };

  return (
    <div className="container-fluid">

      {/* OVERDUE ALERT */}
      {isOverdue && (
        <div className="alert alert-danger erp-overdue-alert">
          ⚠ This followup is overdue.
        </div>
      )}

      {/* FOLLOWUP INFORMATION */}
      <div className="card erp-card erp-section-spacing">
        <div className="card-header erp-gradient-header">
          Followup Information
        </div>

        <div className="card-body">
          <div className="row">

            {/* LEFT COLUMN */}
            <div className="col-md-6">

              <InfoRow label="No" value={data.followup_no} />
              <InfoRow label="Title" value={data.title} />
              <InfoRow label="Description" value={data.description} />

              <div className="row mb-2">
                <div className="col-5 erp-info-label">Status</div>
                <div className="col-7">
                  <span className={`badge erp-status-badge ${getStatusBadgeClass(data.status_code)}`}>
                    {data.status_code}
                  </span>
                </div>
              </div>

              <InfoRow label="Priority" value={data.priority} />
              <InfoRow label="Due Date" value={data.due_date} />

            </div>

            {/* RIGHT COLUMN */}
            <div className="col-md-6">

              <InfoRow label="Domain" value={data.domain_code || "-"} />
              <InfoRow label="Purpose" value={data.purpose_code || "-"} />
              <InfoRow label="Reference Type" value={data.source_module || "-"} />
              <InfoRow label="Reference No" value={data.reference_display || data.source_reference || "-"} />
              
              <InfoRow
                  label="Assigned To"
                  value={
                    data.assigned_to_name ||
                    data.assigned_to ||
                    "Self"
                  }
                />
              <InfoRow label="Created At" value={data.created_at} />

              {data.is_recurring && (
                <InfoRow
                  label="Recurring"
                  value={`Yes (Every ${data.recurrence_days} days)`}
                />
              )}

            </div>

          </div>
        </div>
      </div>

      {/* STATUS CHANGE */}
      <div className="card erp-card erp-section-spacing">
        <div className="card-header erp-gradient-header">
          Change Status
        </div>

        <div className="card-body">
          <div className="row">

            <div className="col-md-4">
              <label>Status</label>
              <select
                className="form-control"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                disabled={data.status_code === "COMPLETED"}
              >
                <option value="">Select Status</option>
                <option value="OPEN">Open</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
              </select>
            </div>

            <div className="col-md-6">
              <label>Remarks</label>
              <input
                className="form-control"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                disabled={data.status_code === "COMPLETED"}
              />
            </div>

            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-primary w-100"
                onClick={handleChangeStatus}
                disabled={saving || data.status_code === "COMPLETED"}
              >
                {saving ? "Saving..." : "Update"}
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* STATUS HISTORY */}
      <div className="card erp-card erp-section-spacing">
        <div className="card-header erp-gradient-header">
          Status History
        </div>

        <div className="card-body p-0">
          <table className="table erp-table mb-0">
            <thead>
              <tr>
                <th>Status</th>
                <th>Remarks</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={i}>
                  <td>{h.status_code}</td>
                  <td>{h.remarks}</td>
                  <td>{h.changed_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ACTION LOGS */}
      <div className="card erp-card">
        <div className="card-header erp-gradient-header">
          Action Logs
        </div>

        <div className="card-body p-0">
          <table className="table erp-table mb-0">
            <thead>
              <tr>
                <th>Action Type</th>
                <th>Result</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {actions.map((a, i) => (
                <tr key={i}>
                  <td>{a.action_type}</td>
                  <td>{a.result_status}</td>
                  <td>{a.executed_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}

/* Reusable Info Row */
const InfoRow = ({ label, value }) => (
  <div className="row mb-2">
    <div className="col-5 erp-info-label">{label}</div>
    <div className="col-7 erp-info-value">{value}</div>
  </div>
);
